import { defineStore } from 'pinia';
import { ref } from 'vue';

import { requestHandler } from '@/services/requestHandler';
import { OnboardingAdminRequests } from '@/services/requests';
import { Onboarding } from '@/services/requests/onboarding/Onboarding.types';
import { IApiResponse } from '@/types/interfaces';
import { PaginationHandler } from '@/utils/paginationUtils';

const COMMENTS_PAGE_SIZE = 3;
const paginationHandler = new PaginationHandler<Onboarding.Comment>(COMMENTS_PAGE_SIZE);

export const useOnboardingCommentsStore = defineStore('OnboardingComments', () => {
  const isLoading = ref(true);
  const currentApplicationId = ref();
  const comments = ref<Onboarding.Comment[]>([]);
  const isAllLoaded = ref(false);

  function $reset(newApplicationId?: string) {
    isLoading.value = true;
    currentApplicationId.value = newApplicationId;
    comments.value = [];
    isAllLoaded.value = false;
  }

  async function getComments(applicationId: string): Promise<IApiResponse<Onboarding.Comment[]>> {
    if (currentApplicationId.value !== applicationId) {
      $reset(applicationId);
      paginationHandler.reconfig((pagination) => OnboardingAdminRequests.Shared.comments.get(applicationId, pagination));
    }
    return requestHandler(paginationHandler.fetchNextPage.bind(paginationHandler), {
      pending: isLoading,
      onSuccess: (response) => {
        comments.value = response;
        isAllLoaded.value = paginationHandler.isAllLoaded;
      },
    });
  }

  async function addComment(applicationId: string, text: string): Promise<IApiResponse<Onboarding.Comment[]>> {
    return requestHandler(async () => paginationHandler.onAddRecord(
      await OnboardingAdminRequests.Shared.comments.add(applicationId, text),
    ), {
      pending: isLoading,
      onSuccess: (response) => {
        comments.value = response;
      },
    });
  }

  async function editComment(
    applicationId: string,
    commentId: string,
    text: string,
  ): Promise<IApiResponse<Onboarding.Comment[]>> {
    return requestHandler(async () => paginationHandler.onEditRecord(
      (record) => record.id === commentId,
      await OnboardingAdminRequests.Shared.comments.edit(applicationId, commentId, text),
    ), {
      pending: isLoading,
      onSuccess: (response) => {
        comments.value = response;
      },
    });
  }

  async function removeComment(
    applicationId: string,
    commentId: string,
  ): Promise<IApiResponse<Onboarding.Comment[]>> {
    return requestHandler(async () => paginationHandler.onRemoveRecord(
      (record) => record.id === commentId,
      await OnboardingAdminRequests.Shared.comments.remove(applicationId, commentId),
    ), {
      pending: isLoading,
      onSuccess: (response) => {
        comments.value = response;
      },
    });
  }

  return {
    comments,
    isLoading,
    isAllLoaded,

    getComments,
    addComment,
    editComment,
    removeComment,

    $reset,
  };
});
